"use client"

import { forwardRef, useMemo } from "react"
import Script from "next/script"
import { env } from "~/env"
import clsx from "clsx"
import { useInView } from "react-intersection-observer"

import type { UnlikelyCartLineInput, UnlikelyProduct } from "@unlikelystudio/commerce-connector"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import { formatCheckoutItems } from "~/lib/shopify/utils/format-checkout-items"
import { soClozCssClassName, soClozDomain } from "~/lib/so-cloz"
import { useGetSoClozAttributes } from "~/lib/so-cloz/hooks/useGetSoClozAttributes"
import useLocale from "~/hooks/useLocale"
import { AddToCartCta, type AddtoCartCtaProps } from "~/components/ui/AddToCartCta"
import BackInStockPopin from "~/components/ui/BackInStockPopin"
import InlineCta from "~/components/ui/InlineCta"
import type { ShopTheLook } from "~/components/ui/Panels/ShopTheLook/_data/serializer"
import type { TProduct, TVariant } from "~/components/ui/ProductHeader/_data/types"
import { StickyAddToCartPopin } from "~/components/ui/ProductHeader/components/ProductDetails/components/ProductDetailsCtas/StickyAddToCartPopin/index.client"
import { ShopTheLookCta } from "~/components/ui/ProductHeader/components/ProductDetails/components/ShopTheLookCta"
import SquareCta from "~/components/ui/SquareCta"
import { useFooterInView } from "~/components/globals/Footer/hooks"
import type { TSizeGuideSlice } from "~/components/slices/SizeGuideSlice/types"
import type { CartLineItemPayload } from "~/providers/GTMTrackingProvider/constants"
import { getProductTrackingDataWithCollections } from "~/providers/GTMTrackingProvider/utils/get-product-tracking-data-with-collections"
import { variantToTrackingData } from "~/providers/GTMTrackingProvider/utils/variant-to-tracking-data"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { useIsFixedPanelOpen } from "~/managers/AddToCartFixedPanelManager/hooks"
import { usePopin } from "~/managers/PopinManager"
import { useActiveVariant } from "~/app/[locale]/products/[slug]/(hooks)/useVariantSwitch"

import { sprinkles } from "~/styles/sprinkles.css"
import { text } from "~/styles/variants"

import * as css from "./styles.css"

export type ProductDetailsCtaProps = PropsWithClassName<{
  serializedProduct: TProduct
  product: UnlikelyProduct
  trackingData: CartLineItemPayload
  sizeGuide: Nullish<TSizeGuideSlice>
  shopTheLookItems: ShopTheLook
}>

export function ProductDetailsCta({
  className,
  shopTheLookItems,
  product,
  trackingData,
  sizeGuide,
  serializedProduct,
}: ProductDetailsCtaProps) {
  const t = useTranslate()
  const locale = useLocale()
  const [primaryButtonRef, primaryButtonInView] = useInView({
    initialInView: true,
    threshold: 0.2,
    rootMargin: "-10% 0px 100% 0px",
  })

  const [footerInView] = useFooterInView()

  const [selectedVariant] = useActiveVariant() ?? []

  const activeVariant = selectedVariant ?? serializedProduct.defaultVariant

  const soClozAttributes = useGetSoClozAttributes(product, "reservation", activeVariant)
  const isOutOfStock =
    (activeVariant?.availableForSale === false || (activeVariant && activeVariant?.quantityAvailable <= 0)) ?? false

  const shouldDisplayStockAlert = isOutOfStock && (serializedProduct.stockAlertEnabled ?? false)
  const productOutOfStock = serializedProduct.variants.every((variant) => variant.availableForSale === false)

  const processedTrackingData =
    trackingData && activeVariant
      ? {
          ...getProductTrackingDataWithCollections(trackingData, product, locale),
          ...variantToTrackingData(activeVariant),
        }
      : null

  const checkoutItems = useMemo(() => {
    if (!activeVariant || !activeVariant.id) return []
    return formatCheckoutItems({
      variant: activeVariant,
      trackingData: processedTrackingData,
    })
  }, [activeVariant?.id])

  const { add: addPopin } = usePopin()
  const openBackInStockPopIn = () => {
    if (activeVariant && serializedProduct.backInStock && activeVariant.id) {
      addPopin(<BackInStockPopin {...serializedProduct.backInStock} defaultVariant={activeVariant.id} />)
    }
  }

  const [isFixedPanelOpen] = useIsFixedPanelOpen()

  return (
    <div className={clsx(css.ProductDetailsCta, className)}>
      <div className={css.background({ active: isFixedPanelOpen })}></div>
      <PrimaryButton
        ref={primaryButtonRef}
        shouldDisplayStockAlert={shouldDisplayStockAlert}
        product={product}
        trackingData={trackingData}
        activeVariants={activeVariant}
        onBackInStockClick={openBackInStockPopIn}
        isOutOfStock={isOutOfStock}
        productOutOfStock={productOutOfStock}
        checkoutItems={checkoutItems}
      />
      <PrimaryButton
        className={clsx(css.primaryButtonFixed({ active: !primaryButtonInView && !footerInView }))}
        shouldDisplayStockAlert={shouldDisplayStockAlert}
        product={product}
        trackingData={trackingData}
        withSizeGuidePopin
        sizeGuideDisabled={serializedProduct.sizeGuideDisabled}
        productType={serializedProduct.productType}
        variants={serializedProduct.variants}
        sizeGuide={sizeGuide}
        collectionsHandles={serializedProduct.collectionsHandles}
        defaultVariant={serializedProduct.defaultVariant}
        canShowFixedPanel={!primaryButtonInView && !footerInView}
        isOutOfStock={isOutOfStock}
        activeVariants={activeVariant}
        onBackInStockClick={openBackInStockPopIn}
        productOutOfStock={productOutOfStock}
        checkoutItems={checkoutItems}
      />

      <div
        className={clsx(
          css.extrasCtas,
          css.extrasCtasWithShopTheLook[shopTheLookItems.length > 0 ? "with" : "without"]
        )}
      >
        <ShopTheLookCta
          serializedProduct={serializedProduct}
          shopTheLookItems={shopTheLookItems}
          shopTheLookIds={serializedProduct.shopTheLookIds}
        />
        {serializedProduct.eReservationEnabled && (
          <InlineCta
            className={clsx(soClozCssClassName, css.eResaCta)}
            color="black"
            icon={{
              name: "Shop",
              direction: "right",
              theme: "black",
            }}
            size="small"
            withLine
            {...soClozAttributes}
          >
            {t("e_reservation")}
          </InlineCta>
        )}
      </div>

      <Script
        id="e-reservation"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `(() => {
            var _scs = []
            _scs.push(["app_key", "${env.NEXT_PUBLIC_SOCLOZ_APP_KEY}"])
            ;(function (d, s, id, _scs) {
              if (d.getElementById(id)) return
              var i,
                sc = d.createElement(s),
                sj = d.getElementsByTagName(s)[0]
                sc.id = id
                sc.async = true
                sc.src = "${soClozDomain}/v4/js/popup.js"
                for (i = 0; i < _scs.length; i++) sc.setAttribute("data-socloz-" + _scs[i][0], _scs[i][1])
                sj?.parentNode?.insertBefore(sc, sj)
            })(document, "script", "soclozjs", _scs)
          })()`,
        }}
      />
    </div>
  )
}

type PrimaryButtonProps = {
  shouldDisplayStockAlert: boolean
  activeVariants: TVariant[] | TVariant | null
  variants?: TVariant[]
  sizeGuideDisabled?: boolean
  sizeGuide?: Nullish<TSizeGuideSlice>
  productType?: TProduct["productType"]
  defaultVariant?: TVariant | null
  collectionsHandles?: TProduct["collectionsHandles"]
  trackingData?: CartLineItemPayload
  onBackInStockClick(): void
  onAddToCart?(): void
  checkoutItems: UnlikelyCartLineInput[]
  withSizeGuidePopin?: boolean
  canShowFixedPanel?: boolean
} & Pick<AddtoCartCtaProps, "productOutOfStock" | "isOutOfStock"> &
  Pick<ProductDetailsCtaProps, "product" | "trackingData">

const PrimaryButton = forwardRef<HTMLDivElement, PropsWithClassName<PrimaryButtonProps>>(
  (
    {
      className,
      shouldDisplayStockAlert,
      isOutOfStock,
      productOutOfStock,
      onBackInStockClick,
      activeVariants,
      checkoutItems,
      variants,
      defaultVariant,
      sizeGuide,
      collectionsHandles,
      productType,
      sizeGuideDisabled,
      product,
      trackingData,
      withSizeGuidePopin,
      canShowFixedPanel,
    },
    ref?
  ) => {
    const t = useTranslate()

    return (
      <div data-comp={"UI/ProductHeader/ProductDetails/PrimaryButton"} ref={ref} className={clsx(className)}>
        {shouldDisplayStockAlert || !activeVariants ? (
          <div className={withSizeGuidePopin ? sprinkles({ p: 10 }) : undefined}>
            <SquareCta
              className={clsx(
                css.cta,
                text({ design: "neue-11-13" }),
                sprinkles({
                  pY: { mobile: 10, tablet: 12 },
                })
              )}
              onClick={() => {
                onBackInStockClick?.()
              }}
              overrideTextPresets
              theme="borderBlack"
              fill
            >
              {t("stock_alert")}
            </SquareCta>
          </div>
        ) : withSizeGuidePopin ? (
          <StickyAddToCartPopin
            product={product}
            shouldDisplayStockAlert={shouldDisplayStockAlert}
            activeVariants={activeVariants}
            variants={variants ?? []}
            sizeGuide={sizeGuide}
            sizeGuideDisabled={sizeGuideDisabled ?? false}
            productType={productType ?? null}
            defaultVariant={defaultVariant ?? null}
            collectionsHandles={collectionsHandles ?? []}
            trackingData={trackingData}
            checkoutItems={checkoutItems}
            canShowFixedPanel={canShowFixedPanel ?? false}
          />
        ) : (
          <AddToCartCta
            className={css.cta}
            overrideTextPresets
            isOutOfStock={isOutOfStock}
            productOutOfStock={productOutOfStock}
            activeVariants={activeVariants}
            checkoutItems={checkoutItems}
          />
        )}
      </div>
    )
  }
)
